import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Outlet } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import queryString from 'query-string';
import Login from '@react-login-page/page3';
import defaultBannerImage from '@react-login-page/page3/bg.jpeg';

// MUI imports
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTie, faShip, faFileArrowUp, faEnvelopeOpenText, faEnvelopeDot, faChartLine, faDatabase, faPlus, faFileCsv, faBug, faRocket, faCalendarXmark, faRobot, faLayerGroup, faTruck, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';

// Local imports
import { useColorMode } from '../../ColorModeContext';

const drawerWidth = 240;

function AdminMain(props) {
    const parsedQuery = queryString.parse(window.location.search) || {};
    
    const [ accesstoken, setAccesstoken ] = useState(sessionStorage.getItem('accesstoken') || (parsedQuery.accesstoken ? parsedQuery.accesstoken : null) || null);
    const [ mobileOpen, setMobileOpen ] = useState(false);
    const [ badges, setBadges ] = useState({ });

    if (!accesstoken) return <AdminLogin setAccesstoken={setAccesstoken} />;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const container = window.document.body;

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <Box
                component='nav'
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
                aria-label='mailbox folders'
            >
            {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    container={container}
                    variant='temporary'
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' }, 
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth,
                            background: 'black',
                            color: 'white',
                        },
                    }}
                >
                    <Sidebar
                        accesstoken={accesstoken}
                        onLogout={() => {
                            setAccesstoken(null);
                        }}
                        badges={badges}
                    />
                </Drawer>
                <Drawer
                    variant='permanent'
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: drawerWidth,
                            background: 'black',
                            color: 'white'
                        },
                    }}
                    open
                >
                    <Sidebar
                        accesstoken={accesstoken}
                        onLogout={() => {
                            setAccesstoken(null);
                        }}
                        badges={badges}
                    />
                </Drawer>
            </Box>
            <Box
                component='main'
                sx={{ flexGrow: 1, p: 0, width: { md: `calc(100% - ${drawerWidth}px)` }, paddingRight: '37px' }}
            >
                <Outlet context={{
                    accesstoken,
                    setBadges,
                    onBadge: () => {},
                }} />
            </Box>
        </Box>
    );
}

function AdminLogin(props) {
    const { setAccesstoken } = props;
    const [ working, setWorking ] = useState(false);
    const [ error, setError ] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        const email = e.target[0].value;
        const password = e.target[1].value;
        
        setWorking(true);
        setError(false);

        try {
            const demoOrgId = '251f5e5d-e272-47e8-beb2-d905c9d05546';
            const { data: accesstoken } = await axios(`/api/login/?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&organizationId=${demoOrgId}`);
            const { data: me } = await axios(`/api/organizations/${demoOrgId}/me?accesstoken=${accesstoken}`);
            setWorking(false);
            if (!me.isAdmin) {
                setError(true);
            } else {
                setAccesstoken(accesstoken);
            }
        } catch (e) {
            setWorking(false);
            setError(true);
        }
    }

    return (
        <form onSubmit={onSubmit}>
            <Login style={{ height: 580 }}>
                <Login.Banner style={{ backgroundImage: `url(${defaultBannerImage})` }} />
                { working && <Login.Email visible={false} /> }
                { !working && <Login.Email index={1}/> }
                { working && <Login.Password visible={false} /> }
                { !working && <Login.Password index={2}/> }
                { working && <Login.Welcome>Logging in...</Login.Welcome> }
                { working && <Login.Submit visible={false} /> }
                {!error && <Login.ButtonAfter visible={false} /> }
                { error && <Login.ButtonAfter>Invalid email/password</Login.ButtonAfter> }
                <Login.Title>We are <b>Monada</b></Login.Title>
            </Login>
        </form>
    );
}

function Sidebar(props) {
    const { onLogout } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const colorMode = useColorMode();
    const location = useLocation();
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);

    const onAccountMenuOpen = (event) => {
        setAccountMenuAnchorEl(event.currentTarget);
        setAccountMenuOpen(true);
    };

    const onAccountMenuClose = () => {
        setAccountMenuOpen(false);
    };

    return (
        <div>
            <div style={{ position: 'fixed', left: '0px', top: '0px', height: '100%', width: '240px', zIndex: '-1', background: theme.palette.panel1, borderRight: `1px solid ${theme.palette.border1}`}}>&nbsp;</div>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant='h6' sx={{ padding: '18px 12px', color: theme.palette.text.primary, cursor: 'pointer' }}>
                    Admin Console
                </Typography>
                <Switch
                    checked={theme.palette.mode === 'dark'}
                    onChange={colorMode.toggleColorMode}
                    color='primary'
                />
            </Box>
            <Box sx={{ width: 'calc(100% - 12px)', display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
                <Typography variant='caption' sx={{ paddingRight: '3px', position: 'relative', top: '1px', color: theme.palette.text.secondary, fontSize: '13px' }}>
                    Powered by
                </Typography>
                <Typography variant='caption' style={{ color: '#605DEC', fontSize: '14px', fontFamily: '\'DM Serif Display\', serif', fontWeight: '400' }}>
                    Monada
                </Typography>
            </Box>
            <div style={{ padding: '0px 12px' }}>
                <hr style={{ opacity: '0.3' }} />
            </div>
            <List sx={{ padding: '0px !important' }}>
                <ListItem button sx={{ color: theme.palette.text.secondary }} onClick={onAccountMenuOpen}>
                    <ListItemIcon sx={{ color: theme.palette.text.secondary }}>
                        <FontAwesomeIcon icon={faUserTie} />
                    </ListItemIcon>
                    <ListItemText primary={'admin@monada.ai'} primaryTypographyProps={{ sx: { fontSize: '13px', color: theme.palette.text.secondary } }} />
                </ListItem>
            </List>
            <Menu
                anchorEl={accountMenuAnchorEl}
                open={accountMenuOpen}
                onClose={onAccountMenuClose}
            >
                <MenuItem onClick={onLogout}>Logout</MenuItem>
            </Menu>
            <div style={{ padding: '0px 12px' }}>
                <hr style={{ opacity: '0.3' }} />
            </div>
            <List>
                {_.compact([
                    {title: 'Sales', icon: null },
                    {title: 'Sales Machine', icon: <FontAwesomeIcon icon={faPaperPlane} /> , path: '/sales-machine' },
                    {title: 'Reports', icon: null },
                    {title: 'Usage Report', icon: <FontAwesomeIcon icon={faUserTie} /> , path: '/usage-report' },
                    {title: 'Elariom', icon: null },
                    {title: 'Rate Imports - Training Data', icon: <FontAwesomeIcon icon={faFileCsv} /> , path: '/rate-imports' },
                    {title: 'Inquiry Training', icon: <FontAwesomeIcon icon={faFileCsv} /> , path: '/inquiry-training' },
                    {title: 'Rate Uploads', icon: null },
                    {title: 'CargoFive Converter', icon: <FontAwesomeIcon icon={faFileCsv} /> , path: '/cargo-five-converter' },
                    {title: 'LCL Tariff Tiers', icon: <FontAwesomeIcon icon={faLayerGroup} /> , path: '/lcl-tariff-tiers' },
                    {title: 'Metro Tariff', icon: <FontAwesomeIcon icon={faTruck} /> , path: '/metro-tariff' },
                    {title: 'Configuration', icon: null },
                    {title: 'Sea Ports', icon: <FontAwesomeIcon icon={faShip} /> , path: '/sea-ports' },
                    {title: 'Accounts/Suppliers', icon: <FontAwesomeIcon icon={faFileArrowUp} /> , path: '/accounts-suppliers' },
                    {title: 'Create Organization', icon: <FontAwesomeIcon icon={faPlus} /> , path: '/create-organization' },
                    {title: 'Monitoring', icon: null },
                    {title: 'FTP Org Updates', icon: <FontAwesomeIcon icon={faEnvelopeOpenText} /> , path: '/ftp-org-updates' },
                    {title: 'Rest Org Updates', icon: <FontAwesomeIcon icon={faEnvelopeDot} /> , path: '/rest-org-updates' },
                    {title: 'CargoFive Monitoring', icon: <FontAwesomeIcon icon={faChartLine} /> , path: '/cargo-five-monitoring' },
                    {title: 'Technical', icon: null },
                    {title: 'Error Emails', icon: <FontAwesomeIcon icon={faBug} /> , path: '/error-emails' },
                    {title: 'Local DB Resets', icon: <FontAwesomeIcon icon={faDatabase} /> , path: '/local-db-resets' },
                    {title: 'Deprecated Rates', icon: <FontAwesomeIcon icon={faCalendarXmark} /> , path: '/deprecated-rates' },
                    {title: 'Releases', icon: <FontAwesomeIcon icon={faRocket} /> , path: '/releases' },
                    {title: 'AI Assistant Logs', icon: <FontAwesomeIcon icon={faRobot} /> , path: '/help-logs' },
                    {title: 'AI Knowledge Base', icon: <FontAwesomeIcon icon={faRobot} />, onClick: () => window.open('https://github.com/Monada-ai/monada/edit/dev/src/client/dashboard/sidepane/help-context.js', '_blank') },
                ]).map((entry, index) => (
                    <ListItem button key={entry.path || entry.title} sx={{ color: theme.palette.text.secondary, background: entry.path && location.pathname.includes(`/admin/${entry.path}`) ? alpha(theme.palette.text.secondary, 0.1) : '' }} disabled={!entry.path && !entry.onClick} onClick={entry.onClick || (() => navigate(`/admin${entry.path}`))}>
                        {entry.icon && <ListItemIcon sx={{ color: theme.palette.text.secondary }}>{entry.icon}</ListItemIcon>}
                        <ListItemText primary={entry.title} primaryTypographyProps={{ sx: { fontSize: '13px', color: theme.palette.text.secondary } }} />
                    </ListItem>
                ))}
            </List>
        </div>
    )
}

export default AdminMain;
